import {useMemo} from 'react';
import {Platform} from 'react-native';
import {useTheme} from './ThemeContext';

/**
 * Happy Services brand palette (mobile blueprint — CLAUDE.md §9): orange, never
 * blue except for the semantic "info" tone. Exposed as constants so bespoke
 * surfaces (tab bar, hero cards, charts) share one source of truth with the
 * theme hook below.
 */
export const BRAND = {
    base: '#F97316',
    light: '#FB923C',
    amber: '#F59E0B',
    dark: '#EA580C',
    deep: '#C2410C',
    gradient: ['#F97316', '#FB923C', '#F59E0B'],
};

/**
 * Deep-blue conversational surface (AI assistant + Team chat). The one place the
 * blueprint allows a non-orange canvas; orange stays the accent on top of it.
 */
export const CONVERSATION = {
    gradient: ['#1B8BEF', '#0C4FB0', '#06255E', '#030B24'],
    locations: [0, 0.32, 0.66, 1],
    surface: 'rgba(255,255,255,0.07)',
    surfaceStrong: 'rgba(255,255,255,0.12)',
    border: 'rgba(255,255,255,0.14)',
    text: '#FFFFFF',
    subText: 'rgba(226,236,255,0.72)',
    muted: 'rgba(200,215,245,0.55)',
};

/**
 * Night-navy "daily activity" canvas of the Sofőr (driver) app.
 */
export const NIGHT = {
    bg: '#15183F',
    bgDeep: '#0F1230',
    card: '#232766',
    cardAlt: '#1C2055',
    border: 'rgba(255,255,255,0.08)',
    text: '#FFFFFF',
    subText: 'rgba(214,220,255,0.72)',
    muted: 'rgba(190,198,245,0.5)',
};

/** Shared corner radii so every surface rounds the same way. */
export const RADII = {sm: 12, md: 16, lg: 20, xl: 26, pill: 999};

/**
 * Hex `#rrggbb` → `rgba(r,g,b,a)`; returns the input unchanged when it is not a
 * 6-digit hex (e.g. already an rgba string).
 * @param {string} hex Colour.
 * @param {number} alpha 0..1.
 * @returns {string} rgba string.
 */
export const withAlpha = (hex, alpha) => {
    const h = String(hex || '').replace('#', '');
    if (!/^[0-9a-fA-F]{6}$/.test(h)) {
        return hex;
    }

    return `rgba(${parseInt(h.slice(0, 2), 16)}, ${parseInt(h.slice(2, 4), 16)}, ${parseInt(h.slice(4, 6), 16)}, ${alpha})`;
};

/**
 * Premium management design tokens derived from the global app theme.
 * Provides a polished, admin-like palette + gradients that look great
 * in both light and dark mode.
 */
export const useManagementTheme = () => {
    const {theme, isDark} = useTheme();

    return useMemo(() => {
        const c = theme.colors;

        const colors = {
            isDark,
            // Surfaces
            bg: isDark ? '#0A0F1E' : '#F5F6FA',
            surface: isDark ? '#131B2F' : '#FFFFFF',
            surfaceAlt: isDark ? '#1A2440' : '#F3F4F8',
            cardElevated: isDark ? '#18223A' : '#FFFFFF',
            // Borders
            border: isDark ? 'rgba(148,163,184,0.14)' : '#ECEEF4',
            borderSoft: isDark ? 'rgba(148,163,184,0.09)' : '#F1F3F8',
            // Text
            heading: isDark ? '#F8FAFC' : '#0F172A',
            text: isDark ? '#E2E8F0' : '#1E293B',
            subText: isDark ? '#94A3B8' : '#64748B',
            muted: isDark ? '#64748B' : '#94A3B8',
            // Brand / accents — the management surfaces use the blueprint orange
            // (the shared app theme keeps the legacy amber for POS).
            primary: BRAND.base,
            primaryLight: BRAND.light,
            primaryDark: BRAND.dark,
            onPrimary: '#FFFFFF',
            accent: c.accent,
            success: isDark ? '#34D399' : '#16A34A',
            warning: isDark ? '#FBBF24' : '#D97706',
            danger: isDark ? '#F87171' : '#DC2626',
            info: isDark ? '#60A5FA' : '#2563EB',
            violet: isDark ? '#A78BFA' : '#7C3AED',
            // Inputs
            inputBg: isDark ? '#101a30' : '#F8FAFC',
            inputBorder: isDark ? 'rgba(148,163,184,0.20)' : '#E2E8F0',
            placeholder: isDark ? '#64748B' : '#9AA7BD',
            // On-color text
            onHero: '#F8FAFC',
            heroSub: 'rgba(255,255,255,0.88)',
            chipBg: isDark ? 'rgba(249,115,22,0.20)' : 'rgba(249,115,22,0.12)',
            chipText: isDark ? '#FDBA74' : '#C2410C',
            // Floating navigation pill + frosted card fills.
            nav: isDark ? '#1B2133' : '#1F2230',
            navInactive: 'rgba(226,232,240,0.62)',
            glass: isDark ? 'rgba(26,36,64,0.78)' : 'rgba(255,255,255,0.78)',
            glassBorder: isDark ? 'rgba(148,163,184,0.18)' : 'rgba(255,255,255,0.9)',
            scrim: isDark ? 'rgba(0,0,0,0.4)' : 'rgba(15,23,42,0.06)',
        };

        const gradients = {
            // Page hero = the brand orange card (Maytech-style highlight); text on
            // it is white. `heroDark` keeps the old navy for opt-in screens.
            hero: BRAND.gradient,
            heroDark: isDark
                ? ['#10182C', '#1B2347', '#312E81']
                : ['#1E293B', '#243049', '#0F172A'],
            brand: BRAND.gradient,
            primary: BRAND.gradient,
            conversation: CONVERSATION.gradient,
            night: [NIGHT.bg, NIGHT.bgDeep],
            success: isDark ? ['#0F766E', '#10B981'] : ['#10B981', '#34D399'],
            info: isDark ? ['#1D4ED8', '#3B82F6'] : ['#2563EB', '#60A5FA'],
            violet: isDark ? ['#5B21B6', '#7C3AED'] : ['#7C3AED', '#A78BFA'],
            danger: isDark ? ['#B91C1C', '#EF4444'] : ['#DC2626', '#F87171'],
            warning: isDark ? ['#B45309', '#F59E0B'] : ['#F59E0B', '#FBBF24'],
            sheen: isDark
                ? ['rgba(249,115,22,0.18)', 'rgba(249,115,22,0)']
                : ['rgba(255,255,255,0.7)', 'rgba(255,255,255,0)'],
        };

        // Solid accent colour per tone — drives the soft tinted icon chips,
        // delta pills and chart series (flatter and calmer than gradient chips).
        const toneSolid = {
            primary: BRAND.base,
            success: colors.success,
            info: colors.info,
            violet: colors.violet,
            danger: colors.danger,
            warning: isDark ? '#FBBF24' : '#F59E0B',
            neutral: colors.subText,
        };
        const toneColor = (tone) => toneSolid[tone] || toneSolid.primary;
        const toneTint = (tone, alpha = isDark ? 0.2 : 0.12) => withAlpha(toneColor(tone), alpha);

        const toneGradient = (tone) => {
            const map = {
                success: gradients.success,
                info: gradients.info,
                violet: gradients.violet,
                danger: gradients.danger,
                warning: gradients.warning,
                primary: gradients.primary,
            };
            return map[tone] || gradients.primary;
        };

        const shadow = (level = 1) => {
            if (isDark) {
                return {
                    shadowColor: '#000',
                    shadowOffset: {width: 0, height: level * 4},
                    shadowOpacity: 0.32,
                    shadowRadius: level * 9,
                    elevation: level * 3,
                };
            }
            return {
                shadowColor: '#1E2A4A',
                shadowOffset: {width: 0, height: level * 5},
                shadowOpacity: 0.07,
                shadowRadius: level * 12,
                elevation: level * 2,
            };
        };

        return {isDark, colors, gradients, toneGradient, toneColor, toneTint, shadow, radii: RADII, platformFont: Platform.OS};
    }, [theme, isDark]);
};

export const formatCurrency = (value) => `${Number(value || 0).toLocaleString('hu-HU')} Ft`;

export const formatNumber = (value) => `${Number(value || 0).toLocaleString('hu-HU')}`;

export const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('hu-HU', {year: 'numeric', month: '2-digit', day: '2-digit'});
};

export const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('hu-HU', {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
};

